<template>
  <div>
    <b-input-group
      id="colour-picker-container"
      :class="['border rounded', { 'focused': isFocused, 'invalid': isValid === false }]"
      size="sm"
    >
      <b-input-group-prepend
        :class="['color-picker', 'border-right']"
        ref="colorpicker"
      >
        <span
          id="selected-colour"
          ref="selected-colour"
          class="selected-colour border-right"
          :style="'background-color: ' + selectedColour"
          @click="setIsPickerDisplayed(!isPickerDisplayed)"
        />
        <chrome-picker
          v-show="isPickerDisplayed"
          :value="colourPickerInitialValue"
          @input="setColours"
          disable-fields
          disable-alpha
        />
        <div
          :class="{'is-paused': disabled}"
        />
      </b-input-group-prepend>
      <b-input
        id="colour-input"
        placeholder="Enter a colour"
        :value="inputValue"
        :disabled="disabled"
        @input="setInputValue($event)"
        @focus="handleFocus()"
        @blur="handleBlur()"
        @keyup.enter="$event.target.blur()"
      />
    </b-input-group>
    <p
      v-if="isValid === false"
      class="mb-0 text-danger"
    >
      Invalid colour
    </p>
    <p
      v-if="isSupported === false"
      class="mb-0 text-danger"
    >
      Unsupported colourspace
    </p>
  </div>
</template>

<script>
import { Chrome } from 'vue-color';
import helper from '@/core/helper';

export default {
  name: 'BaseColourSelect',
  components: {
    'chrome-picker': Chrome,
  },
  props: {
    value: {
      type: [String, Object],
      default: '#000000',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      colourPickerInitialValue: '#000000',
      isPickerDisplayed: false,
      isFocused: false,
      isValid: null,
      isSupported: null,
    };
  },
  computed: {
    selectedColour() {
      return this.inputValue || this.value;
    },
    hasChanged() {
      return this.selectedColour !== this.value;
    },
  },
  methods: {
    setInputValue: helper.debounce(function setInputValue(value) {
      if (!value) {
        this.inputValue = '';
        this.isValid = null;
        this.isSupported = null;
        return;
      }
      const colourspace = this.getColourspace(value);
      if (colourspace === 'rgba' || colourspace === 'hsla') {
        this.isSupported = false;
        return;
      } this.isSupported = true;
      this.isValid = this.checkIsValidColour(colourspace, value.trim());
      if (this.isValid) {
        this.inputValue = value.trim();
        if (this.isPickerDisplayed) this.updateColourPicker();
        if (this.hasChanged) this.emitColour();
      }
    }, 300),
    getColourspace(value) {
      if (value.includes('#')) return 'hex';
      if (value.includes('rgba')) return 'rgba';
      if (value.includes('rgb')) return 'rgb';
      if (value.includes('hsla')) return 'hsla';
      if (value.includes('hsl')) return 'hsl';
      return 'name';
    },
    checkIsValidColour(space, value) {
      if (space === 'name') return CSS.supports('color', value);
      const patterns = {
        hex: '^#(([0-9A-F]{2}){3}|[0-9A-F]{3})$',
        isBelow256: '1?\\d{1,2}||2[0-4]\\d|25[0-5]',
        isBelow360: '[1-2]?\\d{1,2}|3[0-5]\\d',
        isPercentage: '(\\d{1,2}|100)%',
        get rgb() {
          return `^(?:rgb\\()(?:${this.isBelow256}),\\s?(?:${this.isBelow256}),\\s?(?:${this.isBelow256})(?:\\))$`;
        },
        get hsl() {
          return `(?:hsl\\()(?:${this.isBelow360}),\\s?(?:${this.isPercentage}),\\s?(?:${this.isPercentage})(?:\\))`;
        },
      };
      if (!patterns.hasOwnProperty(space)) return false;
      return new RegExp(patterns[space], 'i').test(value);
    },
    setIsPickerDisplayed(isTrue) {
      if (this.disabled) return;
      if (isTrue) {
        this.handleFocus();
        document.addEventListener('click', this.documentClick);
        this.updateColourPicker();
      } else {
        document.removeEventListener('click', this.documentClick);
        if (this.hasChanged) this.emitColour();
        this.handleBlur();
      }
      this.isPickerDisplayed = isTrue;
    },
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },
    handleBlur() {
      this.isFocused = false;
      if (!this.inputValue) this.inputValue = this.value;
      this.$emit('blur');
    },
    documentClick(event) {
      const element = this.$refs.colorpicker;
      if (!element) return;
      if (element !== event.target
          && !element.contains(event.target)
          && event.target.id !== 'colour-input') this.setIsPickerDisplayed(false);
    },
    emitColour() {
      this.$emit('updateValue', this.selectedColour);
      this.$emit('change');
    },
    setColours(colour) {
      if (this.getColourspace(this.selectedColour) === 'rgb') {
        this.inputValue = `rgb(${colour.rgba.r}, ${colour.rgba.g}, ${colour.rgba.b})`;
      } else if (this.getColourspace(this.selectedColour) === 'hsl') {
        const getPercentage = (value) => `${parseInt(value * 100, 10)}%`;
        this.inputValue = `hsl(${parseInt(colour.hsl.h, 10)}, ${getPercentage(colour.hsl.s)}, ${getPercentage(colour.hsl.l)})`;
      } else this.inputValue = colour.hex;
    },
    getHexCodeFromName(targetId) {
      const element = document.getElementById(targetId);
      const bgColour = window.getComputedStyle(element).backgroundColor;
      const [r, g, b] = bgColour.match(/\b\d{1,3}\b/g);
      const hexVal = (c) => (`0${Math.abs(c).toString(16)}`).slice(-2);
      return `#${hexVal(r)}${hexVal(g)}${hexVal(b)}`;
    },
    async updateColourPicker() {
      await this.$nextTick();
      if (this.getColourspace(this.selectedColour) === 'name') this.colourPickerInitialValue = this.getHexCodeFromName('selected-colour');
      else if (this.getColourspace(this.selectedColour) === 'hex') this.colourPickerInitialValue = this.selectedColour;
      else if (this.getColourspace(this.selectedColour) === 'rgb') {
        const [r, g, b] = this.selectedColour.match(/\b\d{1,3}\b/g);
        this.colourPickerInitialValue = {
          r, g, b, a: 1, source: 'rgb',
        };
      } else if (this.getColourspace(this.selectedColour) === 'hsl') {
        const [h, s, l] = this.selectedColour.match(/\b\d{1,3}\b/g);
        this.colourPickerInitialValue = {
          h, s, l, a: 1, source: 'hsl',
        };
      }
    },
  },
  created() {
    this.inputValue = this.value;
  },
};
</script>
<style scoped lang="scss">
#colour-picker-container {
  width: 100%;
  &.focused {
    border-color: $link !important;
    outline: .2rem solid rgba(0, 123, 255, 0.25);
  }
  &.invalid {
    outline: none;
    border-color: $danger !important;
  }
}
#colour-input {
  border: unset;
  &:focus {
    outline: none;
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}
.selected-colour {
  display: block;
  height: 100%;
  width: 31px;
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.vc-chrome {
  position: absolute;
  width: 100%;
  border: 1px solid $gray-300;
  border-radius: $border-radius;
  top: 2.5rem;
  right: 0;
  z-index: 99;
  .vc-chrome-body {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
  }
}
.current-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: #000;
  cursor: pointer;
}
.vc-chrome-alpha-wrap{
  display: none;
}
.vc-chrome-hue-wrap{
  margin-bottom: 0;
  margin-top: 8px;
}
.vc-chrome-active-color{
    border: 1px solid #a9a9a9;
}
.is-paused {
  position: absolute;
  cursor: not-allowed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.text-danger {
  font-size: 12px;
}
</style>
